import axios from '@providers/axiosProvider';
import * as actions from './actions';
import {
  APIGetContractListResponse,
  APIGetMonthlyTablesRequestParams,
  APIGetMonthlyTablesResponse,
  APIPostMonthlyTablePriceRequestParams,
  AddMonthlyTableFormData,
  EditMonthlyTableFormData,
  SearchParameter,
} from './types';
import { APIGetPropertyContractsRequestParams } from '@ducks/property/types';
import { LIST_SIZE_PER_PAGE } from '@utils/constants';
import { confirmDialog } from '@utils/confirmDialog';
import { commonActions } from '@ducks/common';
import { CustomPropertySearchFromData } from '@ducks/customProperty/types';

/**
 * @summary 検索パラメータを元に月次計画詳細のリストを取得
 * @param {number | null} propertyId 建物ID
 * @param {number} year 年
 * @param {number} month 月
 * @param {number} page ページ番号
 * @param {number} sort ソート番号
 * @param {string} keyword 検索キーワード
 * @param {SearchParameter} params 詳細検索条件
 * @param {number[]} tagIds 選択タグIDリスト
 * @param {CustomPropertySearchFromData} customPropertySearchForm カスタムプロパティの検索条件(body)
 * @returns {void}
 */
export const getMonthlyTableAsync =
  (
    propertyId: number | null,
    year: number,
    month: number,
    page: number,
    sort: number,
    keyword: string,
    params: SearchParameter,
    tagIds: number[],
    customPropertySearchForm?: CustomPropertySearchFromData,
  ) =>
  async (dispatch: any) => {
    dispatch(actions.setIsLoading(true));

    const reqParams: APIGetMonthlyTablesRequestParams = {
      ...params,
      year: year,
      month: month,
      page: page,
      sort: sort,
      size_per_page: LIST_SIZE_PER_PAGE,
    };

    if (propertyId != null) reqParams.property_id = propertyId;
    if (keyword) reqParams.keyword = keyword;
    if (tagIds.length) reqParams.tag = tagIds.join(',');

    await axios
      .post<APIGetMonthlyTablesResponse>(
        '/plans/monthly/table/list',
        { ...customPropertySearchForm },
        { params: reqParams },
      )
      .then((response) => {
        const { items, summary, display_params } = response.data.result;
        dispatch(actions.setTables(items));
        dispatch(actions.setSummary(summary));
        dispatch(actions.setDisplayParams(display_params));
      })
      .catch(() => {
        dispatch(actions.setTables([]));
        dispatch(actions.setSummary(null));
        dispatch(actions.setDisplayParams(null));
      });

    dispatch(actions.setIsLoading(false));
  };

/**
 * @summary チェックを元に月次計画詳細のリストを出力
 * @param {number} year 年
 * @param {number} month 月
 * @param {number} sort ソート番号
 * @param {number[]} planIds 計画IDリスト
 * @returns {void}
 */
export const downloadMonthlyTableAsync =
  (year: number, month: number, sort: number, planIds: number[]) => async (dispatch: any) => {
    dispatch(commonActions.setIsProcessing(true));

    const reqParams: APIGetMonthlyTablesRequestParams = {
      year: year,
      month: month,
      sort: sort,
      is_all: false,
    };
    if (planIds.length) reqParams.id = planIds.join(',');

    return await axios
      .post('/plans/monthly/table/export', {}, { params: reqParams })
      .then((response) => {
        return response.data.result;
      })
      .catch(() => {
        return null;
      })
      .finally(() => {
        dispatch(commonActions.setIsProcessing(false));
      });
  };

/**
 * @summary 検索パラメータを元に月次計画詳細のリストを出力
 * @param {number | null} propertyId 建物ID
 * @param {number} year 年
 * @param {number} month 月
 * @param {number} page ページ番号
 * @param {number} sort ソート番号
 * @param {string} keyword 検索キーワード
 * @param {SearchParameter} params 詳細検索条件
 * @param {number[]} tagIds 選択タグIDリスト
 * @param {CustomPropertySearchFromData} customPropertySearchForm カスタムプロパティの検索条件(body)
 * @returns {void}
 */
export const downloadMonthlyTableAllAsync =
  (
    propertyId: number | null,
    year: number,
    month: number,
    page: number,
    sort: number,
    keyword: string,
    params: SearchParameter,
    tagIds: number[],
    customPropertySearchForm?: CustomPropertySearchFromData,
  ) =>
  async (dispatch: any) => {
    dispatch(commonActions.setIsProcessing(true));

    const reqParams: APIGetMonthlyTablesRequestParams = {
      ...params,
      year: year,
      month: month,
      page: page,
      sort: sort,
      is_all: true,
    };

    if (propertyId != null) reqParams.property_id = propertyId;
    if (keyword) reqParams.keyword = keyword;
    if (tagIds.length) reqParams.tag = tagIds.join(',');

    return await axios
      .post('/plans/monthly/table/export', { ...customPropertySearchForm }, { params: reqParams })
      .then((response) => {
        return response.data.result;
      })
      .catch(() => {
        return null;
      })
      .finally(() => {
        dispatch(commonActions.setIsProcessing(false));
      });
  };

/**
 * @summary 建物の契約一覧を取得する
 * @param propertyId 建物ID
 */
export const getPropertyContractList =
  (propertyId: number, page: number, keyword: string) => async (dispatch: any) => {
    dispatch(actions.setIsLoadingContactList(true));

    const reqParams: APIGetPropertyContractsRequestParams = {
      sort: '1',
      page: page,
      keyword: keyword,
      status: 1,
      size_per_page: LIST_SIZE_PER_PAGE,
    };
    await axios
      .get<APIGetContractListResponse>(`/properties/${propertyId}/contracts`, {
        params: reqParams,
      })
      .then((response) => {
        const { contract_list: contractList, display_params: displayParams } = response.data.result;
        dispatch(actions.setContractList(contractList));
        dispatch(actions.setContractDisplayParams(displayParams));
      })
      .catch(() => {
        dispatch(actions.setContractList([]));
        dispatch(actions.setContractDisplayParams(null));
      });

    dispatch(actions.setIsLoadingContactList(false));
  };

/**
 * @summary 新規計画を登録する
 * @param {AddMonthlyTableFormData} params 新規作成フォーム入力データ
 * @returns {Promise<boolean>} 登録成否
 */
export const createMonthlyTableAsync =
  (params: AddMonthlyTableFormData) =>
  async (dispatch: any): Promise<boolean> => {
    dispatch(actions.setIsSubmitting(true));

    return await axios
      .post(`/plans/monthly/table`, {
        ...params,
      })
      .then(() => {
        dispatch(actions.refresh());
        return true;
      })
      .catch(() => {
        return false;
      })
      .finally(() => {
        dispatch(actions.setIsSubmitting(false));
      });
  };

/**
 * @summary 計画を編集する
 * @param {EditMonthlyTableFormData[]} params フォーム入力データ
 * @param {string} confirmText 建物担当者に登録されていないユーザを建物スタッフとして登録するかの確認文言
 * @param {boolean} isSet 建物担当者に登録されていないユーザを建物スタッフとして登録するか
 * @returns {void}
 */
export const editMonthlyTableAsync =
  (params: EditMonthlyTableFormData[], confirmText = '', isSet = false) =>
  async (dispatch: any) => {
    dispatch(actions.setIsSubmitting(true));

    await axios
      .patch(`/plans/monthly/table${isSet ? `?is_set=${true}` : ''}`, {
        plan_list: params,
      })
      .then(async (response) => {
        if (response.status !== 202) {
          dispatch(actions.setIsEditing(false));
          dispatch(actions.refresh());
          return;
        }

        if (!(await confirmDialog(confirmText))) return;
        return dispatch(editMonthlyTableAsync(params, confirmText, true));
      })
      .catch(() => {});

    dispatch(actions.setIsSubmitting(false));
  };

/**
 * @summary 対応状況を変更する
 * @param {{plan_id: number}[]} planIdList 計画IDリスト
 * @param {number} statusId 対応状況ID
 * @returns {void}
 */
export const updateStatusAsync =
  (planIdList: { plan_id: number }[], statusId: number) => async (dispatch: any) => {
    dispatch(actions.setIsSubmitting(true));

    await axios
      .patch('/plans/monthly/table/status', {
        status_id: statusId,
        plan_list: planIdList,
      })
      .then(() => {
        dispatch(actions.refresh());
      })
      .catch(() => {});

    dispatch(actions.setIsSubmitting(false));
  };

/**
 * @summary 監督者を変更する
 * @param {{plan_id: number}[]} planIdList 計画IDリスト
 * @param {{user_id: number}[]} userIdList ユーザーIDリスト
 * @param {string} confirmText 建物担当者に登録されていないユーザを建物スタッフとして登録するかの確認文言
 * @param {boolean} isSet 建物担当者に登録されていないユーザを建物スタッフとして登録するか
 * @returns {Promise<boolean>}
 */
export const updateSupervisorsAsync =
  (
    planIdList: { plan_id: number }[],
    userIdList: { user_id: number }[],
    confirmText = '',
    isSet = false,
  ) =>
  async (dispatch: any): Promise<boolean> => {
    dispatch(actions.setIsSubmitting(true));

    return await axios
      .post(`/plans/monthly/table/supervisors${isSet ? `?is_set=${true}` : ''}`, {
        user_list: userIdList,
        plan_list: planIdList,
      })
      .then(async (response) => {
        if (response.status !== 202) {
          dispatch(actions.refresh());
          return true;
        }

        if (!(await confirmDialog(confirmText))) return false;
        return dispatch(updateSupervisorsAsync(planIdList, userIdList, confirmText, true));
      })
      .catch(() => {
        return false;
      })
      .finally(() => {
        dispatch(actions.setIsSubmitting(false));
      });
  };

/**
 * @summary 作業者を変更する
 * @param {{plan_id: number}[]} planIdList 計画IDリスト
 * @param {{user_id: number}[]} userIdList ユーザーIDリスト
 * @param {string} confirmText 建物担当者に登録されていないユーザを建物スタッフとして登録するかの確認文言
 * @param {boolean} isSet 建物担当者に登録されていないユーザを建物スタッフとして登録するか
 * @returns {Promise<boolean>}
 */
export const updateWorkersAsync =
  (
    planIdList: { plan_id: number }[],
    userIdList: { user_id: number }[],
    confirmText = '',
    isSet = false,
  ) =>
  async (dispatch: any): Promise<boolean> => {
    dispatch(actions.setIsSubmitting(true));

    return await axios
      .post(`/plans/monthly/table/workers${isSet ? `?is_set=${true}` : ''}`, {
        user_list: userIdList,
        plan_list: planIdList,
      })
      .then(async (response) => {
        if (response.status !== 202) {
          dispatch(actions.refresh());
          return true;
        }

        if (!(await confirmDialog(confirmText))) return false;
        return dispatch(updateWorkersAsync(planIdList, userIdList, confirmText, true));
      })
      .catch(() => {
        return false;
      })
      .finally(() => {
        dispatch(actions.setIsSubmitting(false));
      });
  };

/**
 * @summary 契約先担当者を変更する
 * @param {{plan_id: number}[]} planIdList 計画IDリスト
 * @param {{user_id: number}[]} UserIdList 連絡先IDリスト
 * @returns {Promise<boolean>}
 */
export const updateContractorsAsync =
  (planIdList: { plan_id: number }[], UserIdList: { user_id: number }[]) =>
  async (dispatch: any): Promise<boolean> => {
    dispatch(actions.setIsSubmitting(true));

    return await axios
      .post('/plans/monthly/table/contractors', {
        user_list: UserIdList,
        plan_list: planIdList,
      })
      .then(() => {
        dispatch(actions.refresh());
        return true;
      })
      .catch(() => {
        return false;
      })
      .finally(() => {
        dispatch(actions.setIsSubmitting(false));
      });
  };

/**
 * @summary テナント担当者を変更する
 * @param {{plan_id: number}[]} planIdList 計画IDリスト
 * @param {{user_id: number}[]} UserIdList 連絡先IDリスト
 * @returns {Promise<boolean>}
 */
export const updateContactsAsync =
  (planIdList: { plan_id: number }[], UserIdList: { user_id: number }[]) =>
  async (dispatch: any): Promise<boolean> => {
    dispatch(actions.setIsSubmitting(true));

    return await axios
      .post('/plans/monthly/table/contacts', {
        user_list: UserIdList,
        plan_list: planIdList,
      })
      .then(() => {
        dispatch(actions.refresh());
        return true;
      })
      .catch(() => {
        return false;
      })
      .finally(() => {
        dispatch(actions.setIsSubmitting(false));
      });
  };

/**
 * @summary 協力会社を変更する
 * @param {{plan_id: number}[]} planIdList 計画IDリスト
 * @param {{company_id: number}[]} companyIdList 企業IDリスト
 * @returns {Promise<boolean>}
 */
export const updateCompaniesAsync =
  (planIdList: { plan_id: number }[], companyIdList: { company_id: number }[]) =>
  async (dispatch: any): Promise<boolean> => {
    dispatch(actions.setIsSubmitting(true));

    return await axios
      .post('/plans/monthly/table/companies', {
        company_list: companyIdList,
        plan_list: planIdList,
      })
      .then(() => {
        dispatch(actions.refresh());
        return true;
      })
      .catch(() => {
        return false;
      })
      .finally(() => {
        dispatch(actions.setIsSubmitting(false));
      });
  };

/**
 * @summary 予定調整先を変更する
 * @param {{plan_id: number}[]} planIdList 計画IDリスト
 * @param {{user_id: number}[]} userIdList ユーザーIDリスト
 * @param {string} confirmText 建物担当者に登録されていないユーザを建物スタッフとして登録するかの確認文言
 * @param {boolean} isSet 建物担当者に登録されていないユーザを建物スタッフとして登録するか
 * @returns {Promise<boolean>}
 */
export const updateAdjustmentsAsync =
  (
    planIdList: { plan_id: number }[],
    userIdList: { user_id: number }[],
    confirmText = '',
    isSet = false,
  ) =>
  async (dispatch: any): Promise<boolean> => {
    dispatch(actions.setIsSubmitting(true));

    return await axios
      .post(`plans/monthly/table/adjustment${isSet ? `?is_set=${true}` : ''}`, {
        user_list: userIdList,
        plan_list: planIdList,
      })
      .then(async (response) => {
        if (response.status !== 202) {
          dispatch(actions.refresh());
          return true;
        }

        if (!(await confirmDialog(confirmText))) return false;
        return dispatch(updateAdjustmentsAsync(planIdList, userIdList, confirmText, true));
      })
      .catch(() => {
        return false;
      })
      .finally(() => {
        dispatch(actions.setIsSubmitting(false));
      });
  };

/**
 * @summary 予定調整先を削除する
 * @param {{plan_id: number}[]} planIdList 計画IDリスト
 * @returns {void}
 */
export const deleteAdjustmentsAsync =
  (planIdList: { plan_id: number }[]) => async (dispatch: any) => {
    dispatch(actions.setIsSubmitting(true));

    await axios
      .post(`plans/monthly/table/adjustment/reset`, {
        plan_list: planIdList,
      })
      .then(() => {
        dispatch(actions.refresh());
      })
      .catch(() => {});

    dispatch(actions.setIsSubmitting(false));
  };

/**
 * @summary 販売結果を登録する
 * @param {APIPostMonthlyTablePriceRequestParams} params リクエストパラメータ
 * @returns {Promise<boolean>} 登録成否
 */
export const updateSalesResult =
  (params: APIPostMonthlyTablePriceRequestParams) =>
  async (dispatch: any): Promise<boolean> => {
    dispatch(actions.setIsSubmitting(true));

    return await axios
      .patch(`plans/monthly/table/price`, {
        ...params,
      })
      .then(() => {
        dispatch(actions.refresh());
        return true;
      })
      .catch(() => {
        return false;
      })
      .finally(() => {
        dispatch(actions.setIsSubmitting(false));
      });
  };

/**
 * @summary 計画を削除する
 * @param {{plan_id: number}[]} planIdList 計画IDリスト
 * @returns {void}
 */
export const deletePlanListAsync = (planIdList: { plan_id: number }[]) => async (dispatch: any) => {
  dispatch(actions.setIsSubmitting(true));

  await axios
    .patch('/plans/monthly/table/delete', {
      plan_list: planIdList,
    })
    .then(() => {
      dispatch(actions.refresh());
    })
    .catch(() => {});

  dispatch(actions.setIsSubmitting(false));
};
